import React, { useEffect, useMemo, useState } from 'react';
import * as api from '@/api/client';
import {
  ArrowLeft, ChevronRight, ChevronDown, Pencil, Save, History,
  Play, Download, Upload, Loader2, AlertTriangle, CheckCircle, X, RotateCcw,
} from 'lucide-react';
import AppFooter from '@/components/layout/AppFooter';

import { apiErrorText } from '../../lib/describeApiError';
// ─── Types mirroring the versioned prompt shape from prompts_config.py ──

interface PromptVersion {
  version: number;
  template: string;
  system_message?: string;    // v3 per-prompt system-role text (empty on v2)
  notes: string;
  created_at: string;
}

interface PromptEntry {
  id: string;
  name: string;
  stage: string;
  tier: string;
  template: string;           // active template (legacy mirror)
  system_message?: string;    // v3 system-role text; "" means fall back to provider_system_default
  variables: string[];
  active_version: number;
  versions: PromptVersion[];
}

type PromptMap = Record<string, PromptEntry>;

const STAGE_ORDER = ['stage_1', 'stage_2', 'stage_2_5', 'stage_3', 'stage_4'];

function splitKey(key: string): { base: string; jurisdiction: string | null } {
  const idx = key.indexOf('.');
  if (idx < 0) return { base: key, jurisdiction: null };
  return { base: key.slice(0, idx), jurisdiction: key.slice(idx + 1) };
}

function stageOf(entry: PromptEntry): string {
  return entry.stage || 'stage_other';
}

// Simple line-based diff — a full LCS isn't needed for small templates.
function diffLines(a: string, b: string): { sign: ' ' | '-' | '+'; text: string }[] {
  const aLines = a.split('\n');
  const bLines = b.split('\n');
  const out: { sign: ' ' | '-' | '+'; text: string }[] = [];
  const max = Math.max(aLines.length, bLines.length);
  for (let i = 0; i < max; i++) {
    const al = aLines[i] ?? '';
    const bl = bLines[i] ?? '';
    if (al === bl) {
      out.push({ sign: ' ', text: al });
    } else {
      if (al) out.push({ sign: '-', text: al });
      if (bl) out.push({ sign: '+', text: bl });
    }
  }
  return out;
}

function ValidationPanel({
  validating, validation,
}: {
  validating: boolean;
  validation: api.PromptValidationResult | null;
}) {
  if (!validation) {
    return (
      <aside
        data-testid="prompt-validation-panel"
        className="lg:col-span-2 border border-zinc-200 rounded-sm bg-zinc-50/50 p-3 text-xs text-zinc-500"
      >
        {validating
          ? <span className="flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Validating…</span>
          : 'No protection contract for this prompt.'}
      </aside>
    );
  }

  const p = validation.protection;
  const found = new Set<string>(
    (validation.blocking.concat(validation.warnings, validation.unknown))
      .filter(i => i.type === 'placeholder')
      .map(i => i.item.replace(/[{}]/g, '')),
  );
  const missingRequired = new Set<string>(
    validation.blocking.filter(b => b.type === 'placeholder').map(b => b.item.replace(/[{}]/g, '')),
  );
  const missingOptional = new Set<string>(
    validation.warnings.filter(b => b.type === 'placeholder').map(b => b.item.replace(/[{}]/g, '')),
  );
  const missingSections = new Set<string>(
    validation.blocking.filter(b => b.type === 'section').map(b => b.item),
  );
  const missingJson = new Set<string>(
    validation.blocking.filter(b => b.type === 'json_instruction').map(b => b.item),
  );
  const unknownPh = validation.unknown.filter(u => u.type === 'placeholder');

  const row = (ok: boolean, label: string, hint: string, tone: 'required' | 'optional' | 'unknown' = 'required') => (
    <li key={label} className="flex items-start gap-2 text-xs">
      <span className={
        'mt-0.5 shrink-0 inline-block h-3 w-3 rounded-full ' +
        (ok
          ? 'bg-green-500'
          : tone === 'optional'
            ? 'bg-zinc-300'
            : tone === 'unknown'
              ? 'bg-orange-400'
              : 'bg-red-500')
      }/>
      <span className={ok ? 'text-zinc-700' : tone === 'optional' ? 'text-zinc-500' : 'text-red-800 font-medium'}>
        <span className="font-mono">{label}</span>
        <span className="text-zinc-500 ml-1 font-normal">{hint}</span>
      </span>
    </li>
  );

  return (
    <aside
      data-testid="prompt-validation-panel"
      className="lg:col-span-2 border border-zinc-200 rounded-sm bg-white p-3 space-y-4"
    >
      <div className="flex items-center gap-2 text-xs">
        {validation.valid ? (
          <>
            <CheckCircle size={14} className="text-green-600" />
            <span className="text-green-800 font-medium">All required content present</span>
          </>
        ) : (
          <>
            <AlertTriangle size={14} className="text-red-600" />
            <span className="text-red-800 font-medium">
              {validation.blocking.length} blocking issue{validation.blocking.length === 1 ? '' : 's'}
            </span>
          </>
        )}
        {validating && <Loader2 size={12} className="animate-spin text-zinc-400 ml-auto" />}
      </div>

      {(p.required_placeholders.length > 0 || p.optional_placeholders.length > 0) && (
        <div>
          <h4 className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Placeholders</h4>
          <ul className="space-y-1">
            {p.required_placeholders.map(name => row(
              !missingRequired.has(name),
              `{${name}}`,
              missingRequired.has(name) ? ' — required, MISSING' : ' — required, present',
              'required',
            ))}
            {p.optional_placeholders.map(name => row(
              found.has(name) && !missingOptional.has(name),
              `{${name}}`,
              missingOptional.has(name) ? ' — optional, not present (allowed)' : ' — optional, present',
              'optional',
            ))}
          </ul>
        </div>
      )}

      {p.required_sections.length > 0 && (
        <div>
          <h4 className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Section Labels</h4>
          <ul className="space-y-1">
            {p.required_sections.map(sec => row(
              !missingSections.has(sec),
              sec,
              missingSections.has(sec) ? ' — required, MISSING' : ' — required, present',
              'required',
            ))}
          </ul>
        </div>
      )}

      {validation.expects_json && p.required_json_instructions.length > 0 && (
        <div>
          <h4 className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">JSON Instructions</h4>
          <ul className="space-y-1">
            {p.required_json_instructions.map(ins => row(
              !missingJson.has(ins),
              `"${ins.length > 60 ? ins.slice(0, 57) + '…' : ins}"`,
              missingJson.has(ins) ? ' — required, MISSING' : ' — required, present',
              'required',
            ))}
          </ul>
        </div>
      )}

      {unknownPh.length > 0 && (
        <div>
          <h4 className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Unknown</h4>
          <ul className="space-y-1">
            {unknownPh.map(u => row(
              false, u.item, ' — not recognised', 'unknown',
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}


function SystemMessageView({ systemMessage }: { systemMessage: string }) {
  // v3 prompts stash the LLM system-role text in a separate column.
  // When empty, the render path falls back to provider_system_default —
  // render a muted hint so admins can tell at a glance which prompts
  // are on the v3 schema and which are still v2.
  if (!systemMessage) {
    return (
      <div
        data-testid="prompt-system-message-empty"
        className="flex items-center gap-2"
      >
        <h4 className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          System Message
        </h4>
        <span className="text-[11px] text-zinc-400 italic">
          (using provider default)
        </span>
      </div>
    );
  }
  const hasSkillGuidance = systemMessage.includes('{skill_guidance}');
  return (
    <div data-testid="prompt-system-message-view">
      <div className="mb-1 flex items-center justify-between">
        <h4 className="text-[10px] font-mono uppercase tracking-widest text-blue-700">
          System Message
        </h4>
        {hasSkillGuidance && (
          <span
            data-testid="prompt-skill-guidance-badge"
            className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-800 font-medium"
          >
            Contains skill guidance
          </span>
        )}
      </div>
      <pre
        className="bg-blue-50/60 border border-blue-200 rounded-sm p-3 text-xs font-mono whitespace-pre-wrap leading-relaxed max-h-[30vh] overflow-y-auto text-blue-950"
      >
        {systemMessage}
      </pre>
    </div>
  );
}


export default function PromptAdmin({ onBack }: { onBack: () => void }) {
  const [prompts, setPrompts] = useState<PromptMap>({});
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [jurisFilter, setJurisFilter] = useState<'' | 'EP' | 'US' | 'IN'>('');
  const [editMode, setEditMode] = useState(false);
  const [draftTemplate, setDraftTemplate] = useState('');
  const [draftSystemMessage, setDraftSystemMessage] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  // Phase 13B-second Chunk 2 — Reset all to seed confirmation state.
  const [confirmResetAll, setConfirmResetAll] = useState(false);
  const [resettingAll, setResettingAll] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [openStages, setOpenStages] = useState<Record<string, boolean>>({
    stage_1: true, stage_2: true, stage_2_5: true, stage_3: true, stage_4: true,
  });
  const [testOpen, setTestOpen] = useState(false);

  // ── Prompt-protection validator state ─────────────────────────
  const [validation, setValidation] = useState<api.PromptValidationResult | null>(null);
  const [validating, setValidating] = useState(false);
  const [postSaveWarnings, setPostSaveWarnings] = useState<api.PromptValidationIssue[]>([]);
  const [postSaveUnknown, setPostSaveUnknown] = useState<api.PromptValidationIssue[]>([]);
  const [dismissedPostSaveIds, setDismissedPostSaveIds] = useState<Set<string>>(new Set());
  const [restoring, setRestoring] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getPrompts();
      setPrompts(data.prompts || {});
    } catch (e: any) {
      setError(e?.message || 'Failed to load prompts');
    }
    setLoading(false);
  };

  const activeEntry: PromptEntry | null = activeKey ? prompts[activeKey] : null;
  const activeVersion = activeEntry?.versions.find(v => v.version === activeEntry?.active_version);

  const groupedByStage = useMemo(() => {
    const g: Record<string, { key: string; entry: PromptEntry }[]> = {};
    for (const [key, rawEntry] of Object.entries(prompts)) {
      const entry = rawEntry as PromptEntry;
      const { jurisdiction } = splitKey(key);
      if (jurisFilter && jurisdiction !== jurisFilter) continue;
      const stage = stageOf(entry);
      (g[stage] ||= []).push({ key, entry });
    }
    // Keep stages in a known order
    const ordered: Record<string, { key: string; entry: PromptEntry }[]> = {};
    for (const s of STAGE_ORDER) if (g[s]) ordered[s] = g[s].sort((a, b) => a.key.localeCompare(b.key));
    for (const s of Object.keys(g)) if (!ordered[s]) ordered[s] = g[s].sort((a, b) => a.key.localeCompare(b.key));
    return ordered;
  }, [prompts, jurisFilter]);

  // Debounced live validation. Runs 300ms after the drafter stops
  // typing so we're not firing a backend call on every keystroke but
  // still feel instant. Clears when edit mode exits.
  useEffect(() => {
    if (!editMode || !activeKey) {
      setValidation(null);
      return;
    }
    const t = setTimeout(async () => {
      setValidating(true);
      try {
        const v = await api.validatePromptContent(activeKey, draftTemplate);
        setValidation(v);
      } catch {
        setValidation(null);
      } finally {
        setValidating(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [editMode, activeKey, draftTemplate]);

  const blockingCount = validation?.blocking.length || 0;

  const startEdit = () => {
    if (!activeEntry || !activeVersion) return;
    setEditMode(true);
    setDraftTemplate(activeVersion.template);
    setDraftSystemMessage(
      activeVersion.system_message ?? activeEntry.system_message ?? '',
    );
    setDraftNotes('');
    setError(null);
    setPostSaveWarnings([]);
    setPostSaveUnknown([]);
    setDismissedPostSaveIds(new Set());
  };

  const handleRestoreDefault = async () => {
    if (!activeKey) return;
    setRestoring(true);
    setError(null);
    try {
      await api.restorePromptDefault(activeKey);
      setFlash(`Restored ${activeKey} to seed default`);
      setConfirmRestore(false);
      setEditMode(false);
      await load();
      setTimeout(() => setFlash(null), 3000);
    } catch (e: any) {
      setError(apiErrorText(e, 'Restore failed'));
    } finally {
      setRestoring(false);
    }
  };

  const cancelEdit = () => {
    setEditMode(false);
    setDraftTemplate('');
    setDraftSystemMessage('');
    setDraftNotes('');
  };

  const handleSaveNewVersion = async () => {
    if (!activeKey) return;
    setSaving(true);
    setError(null);
    try {
      const resp: any = await api.updatePrompt(
        activeKey,
        draftTemplate,
        draftNotes,
        draftSystemMessage,
      );
      setFlash(`Saved new version of ${activeKey}`);
      setEditMode(false);
      // Backend echoes warnings + unknown items so we can surface
      // dismissable banners without re-running the validator.
      setPostSaveWarnings((resp && resp.warnings) || []);
      setPostSaveUnknown((resp && resp.unknown) || []);
      setDismissedPostSaveIds(new Set());
      await load();
      setTimeout(() => setFlash(null), 3000);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      // 422 with structured payload → surface blocking list in the
      // validation panel (already visible) + a terse inline error.
      if (
        e?.response?.status === 422
        && typeof detail === 'object'
        && Array.isArray(detail?.blocking)
      ) {
        setValidation({
          valid: false,
          blocking: detail.blocking,
          warnings: detail.warnings || [],
          unknown: detail.unknown || [],
          expects_json: validation?.expects_json || false,
          protection: validation?.protection || {
            required_placeholders: [],
            optional_placeholders: [],
            required_sections: [],
            optional_sections: [],
            required_json_instructions: [],
          },
        });
        setError(
          detail.message ||
          'Required content missing — fix the flagged items and save again.',
        );
      } else {
        setError(
          (typeof detail === 'string' ? detail : undefined)
          || e?.message
          || 'Save failed',
        );
      }
    }
    setSaving(false);
  };

  const handleActivate = async (version: number) => {
    if (!activeKey) return;
    try {
      await api.activatePromptVersion(activeKey, version);
      setFlash(`Activated v${version} of ${activeKey}`);
      await load();
      setTimeout(() => setFlash(null), 3000);
    } catch (e: any) {
      setError(apiErrorText(e, 'Activation failed'));
    }
  };

  const handleExport = async () => {
    try {
      const data = await api.exportPromptLibrary();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `claimstudio_prompts_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e: any) {
      setError(e?.message || 'Export failed');
    }
  };

  const handleImport = async (file: File, activate: boolean) => {
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const res = await api.importPromptLibrary(payload, activate);
      setFlash(`Imported ${res.imported} prompt(s)${activate ? ' (activated)' : ' — not activated'}`);
      await load();
      setTimeout(() => setFlash(null), 3000);
    } catch (e: any) {
      setError(apiErrorText(e, 'Import failed'));
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="prompt-admin">
      <div className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} data-testid="prompt-admin-back" className="text-zinc-500 hover:text-zinc-900 transition-colors">
              <ArrowLeft size={18} />
            </button>
            <span className="text-lg font-semibold tracking-tight text-zinc-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Prompt Management
            </span>
            <span className="text-xs text-zinc-500">
              {Object.keys(prompts).length} prompts
            </span>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="prompt-jurisdiction-filter"
              value={jurisFilter}
              onChange={(e) => setJurisFilter(e.target.value as any)}
              className="text-sm bg-white border border-zinc-300 rounded-sm px-2 py-1.5"
            >
              <option value="">All jurisdictions</option>
              <option value="EP">EP only</option>
              <option value="US">US only</option>
              <option value="IN">IN only</option>
            </select>
            <button
              data-testid="prompt-export-button"
              onClick={handleExport}
              className="text-xs flex items-center gap-1 px-3 py-1.5 border border-zinc-300 rounded-sm hover:bg-zinc-50"
            >
              <Download size={14} /> Export all
            </button>
            {/* Phase 13B-second Chunk 2 — Reset all to seed v3.
                Confirmation modal lives below; clicking opens it
                rather than firing the destructive POST directly. */}
            <button
              data-testid="prompt-reset-all-button"
              onClick={() => setConfirmResetAll(true)}
              className="text-xs flex items-center gap-1 px-3 py-1.5 border border-rose-300 text-rose-700 rounded-sm hover:bg-rose-50"
              title="Hard-reset every prompt to seed v3 defaults; preserves edit history."
            >
              <RotateCcw size={14} /> Reset all to seed
            </button>
            <label className="text-xs flex items-center gap-1 px-3 py-1.5 border border-zinc-300 rounded-sm hover:bg-zinc-50 cursor-pointer">
              <Upload size={14} /> Import
              <input
                data-testid="prompt-import-input"
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImport(f, false);
                  e.currentTarget.value = '';
                }}
              />
            </label>
          </div>
        </div>
      </div>

      {flash && (
        <div data-testid="prompt-admin-flash" className="max-w-7xl mx-auto mt-3 px-6">
          <div className="p-2 bg-green-50 border border-green-200 rounded-sm text-xs text-green-800 flex items-center gap-2">
            <CheckCircle size={14} /> {flash}
          </div>
        </div>
      )}
      {error && (
        <div data-testid="prompt-admin-error" className="max-w-7xl mx-auto mt-3 px-6">
          <div className="p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-800 flex items-center gap-2">
            <AlertTriangle size={14} /> {error}
          </div>
        </div>
      )}
      {(postSaveWarnings.length + postSaveUnknown.length) > 0 && (
        <div data-testid="prompt-post-save-banners" className="max-w-7xl mx-auto mt-3 px-6 space-y-1">
          {postSaveWarnings.concat(postSaveUnknown).map((w, idx) => {
            const id = `${w.type}:${w.item}:${idx}`;
            if (dismissedPostSaveIds.has(id)) return null;
            return (
              <div
                key={id}
                data-testid={`prompt-post-save-banner-${idx}`}
                className="p-2 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900 flex items-start gap-2"
              >
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span className="flex-1">Saved. {w.message}</span>
                <button
                  type="button"
                  onClick={() => setDismissedPostSaveIds((s) => {
                    const n = new Set<string>();
                    s.forEach(v => n.add(v));
                    n.add(id);
                    return n;
                  })}
                  className="text-amber-700 hover:text-amber-900"
                  aria-label="Dismiss"
                >
                  <X size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-12 gap-6">
        {/* LEFT RAIL */}
        <aside className="col-span-4 border border-zinc-200 rounded-sm bg-white p-3" data-testid="prompt-left-rail">
          {loading ? (
            <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>
          ) : Object.keys(groupedByStage).length === 0 ? (
            <div className="text-xs text-zinc-400 italic">No prompts match the filter.</div>
          ) : (
            Object.entries(groupedByStage).map(([stage, items]) => (
              <div key={stage} className="mb-2">
                <button
                  onClick={() => setOpenStages(s => ({ ...s, [stage]: !s[stage] }))}
                  className="w-full flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 px-2 py-1"
                >
                  {openStages[stage] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  {stage}
                </button>
                {openStages[stage] && (
                  <div className="pl-3 mt-1 space-y-0.5">
                    {(items as { key: string; entry: PromptEntry }[]).map(({ key, entry }) => {
                      const { jurisdiction } = splitKey(key);
                      const isActive = key === activeKey;
                      return (
                        <button
                          key={key}
                          data-testid={`prompt-list-${key}`}
                          onClick={() => { setActiveKey(key); setEditMode(false); setShowHistory(false); }}
                          className={`w-full text-left px-2 py-1.5 rounded-sm text-xs flex items-center gap-2 transition-colors ${
                            isActive ? 'bg-blue-50 text-[#002FA7]' : 'text-zinc-700 hover:bg-zinc-50'
                          }`}
                        >
                          <span className="truncate flex-1">
                            {entry.name}
                            {jurisdiction && (
                              <span className="ml-1 text-zinc-400">· {jurisdiction}</span>
                            )}
                          </span>
                          <span className="text-zinc-400 font-mono">v{entry.active_version}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))
          )}
        </aside>

        {/* RIGHT PANE */}
        <section className="col-span-8 border border-zinc-200 rounded-sm bg-white p-6" data-testid="prompt-detail-pane">
          {!activeEntry ? (
            <p className="text-sm text-zinc-500">Select a prompt from the list to view or edit it.</p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-base font-medium text-zinc-900">{activeEntry.name}</h3>
                  <p className="text-xs text-zinc-500 font-mono">
                    key={activeEntry.id} · stage={activeEntry.stage} · tier={activeEntry.tier} · active=v{activeEntry.active_version}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {!editMode && (
                    <>
                      <button
                        data-testid="prompt-test-button"
                        onClick={() => setTestOpen(true)}
                        className="text-xs flex items-center gap-1 px-3 py-1.5 border border-zinc-300 rounded-sm hover:bg-zinc-50"
                      >
                        <Play size={12} /> Test prompt
                      </button>
                      <button
                        data-testid="prompt-history-button"
                        onClick={() => setShowHistory(h => !h)}
                        className="text-xs flex items-center gap-1 px-3 py-1.5 border border-zinc-300 rounded-sm hover:bg-zinc-50"
                      >
                        <History size={12} /> History ({activeEntry.versions.length})
                      </button>
                      <button
                        data-testid="prompt-edit-button"
                        onClick={startEdit}
                        className="text-xs flex items-center gap-1 px-3 py-1.5 bg-[#002FA7] text-white rounded-sm hover:bg-[#002277]"
                      >
                        <Pencil size={12} /> Edit as new version
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="mb-4 text-xs text-zinc-600">
                Variables expected:{' '}
                <span className="font-mono">
                  {activeEntry.variables.length
                    ? activeEntry.variables.map(v => `{${v}}`).join(', ')
                    : '(none — literal prompt)'}
                </span>
              </div>

              {editMode ? (
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                  <div className="lg:col-span-3">
                    <div className="mb-1 flex items-center justify-between">
                      <label
                        className="text-[10px] font-mono uppercase tracking-widest text-blue-700"
                        htmlFor="prompt-edit-system-message"
                      >
                        System Message
                      </label>
                      {draftSystemMessage.includes('{skill_guidance}') && (
                        <span
                          data-testid="prompt-edit-skill-guidance-badge"
                          className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-800 font-medium"
                        >
                          Contains skill guidance
                        </span>
                      )}
                    </div>
                    <textarea
                      id="prompt-edit-system-message"
                      data-testid="prompt-edit-system-message"
                      value={draftSystemMessage}
                      onChange={(e) => setDraftSystemMessage(e.target.value)}
                      rows={8}
                      placeholder="Leave empty to fall back to provider_system_default."
                      className="w-full bg-blue-50/40 border border-blue-200 rounded-sm px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y"
                    />
                    <label
                      className="block mt-3 mb-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500"
                      htmlFor="prompt-edit-textarea"
                    >
                      User Template
                    </label>
                    <textarea
                      id="prompt-edit-textarea"
                      data-testid="prompt-edit-textarea"
                      value={draftTemplate}
                      onChange={(e) => setDraftTemplate(e.target.value)}
                      rows={24}
                      className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y"
                    />
                    <label className="block mt-3 text-xs text-zinc-500">Notes on this version</label>
                    <input
                      data-testid="prompt-edit-notes"
                      value={draftNotes}
                      onChange={(e) => setDraftNotes(e.target.value)}
                      placeholder="e.g., tightened EP two-part guidance"
                      className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    />
                    <div className="mt-3 flex gap-2 items-center flex-wrap">
                      <button
                        data-testid="prompt-save-version-button"
                        onClick={handleSaveNewVersion}
                        disabled={saving || blockingCount > 0}
                        title={
                          blockingCount > 0
                            ? `${blockingCount} blocking issue${blockingCount === 1 ? '' : 's'}: ${(validation?.blocking || []).map(b => b.item).join(', ')}`
                            : 'Save this version'
                        }
                        className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-3 py-1.5 text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Save as v{(activeEntry.versions[activeEntry.versions.length - 1]?.version || 0) + 1}
                      </button>
                      <button onClick={cancelEdit} className="bg-white text-zinc-700 border border-zinc-300 rounded-sm px-3 py-1.5 text-sm">
                        Cancel
                      </button>
                      {!confirmRestore ? (
                        <button
                          type="button"
                          data-testid="prompt-restore-default-button"
                          onClick={() => setConfirmRestore(true)}
                          disabled={restoring}
                          className="bg-white text-red-700 border border-red-200 hover:bg-red-50 rounded-sm px-3 py-1.5 text-sm flex items-center gap-1 disabled:opacity-50 ml-auto"
                          title="Replace with the seed-default content"
                        >
                          <RotateCcw size={12} /> Restore Default
                        </button>
                      ) : (
                        <div
                          data-testid="prompt-restore-confirm"
                          className="ml-auto flex items-center gap-2 bg-red-50 border border-red-200 rounded-sm px-2 py-1.5 text-xs text-red-800"
                        >
                          <span>Restore this prompt to its default? Your current edits will be lost.</span>
                          <button
                            data-testid="prompt-restore-confirm-yes"
                            onClick={handleRestoreDefault}
                            disabled={restoring}
                            className="px-2 py-0.5 rounded bg-red-600 text-white text-[11px] hover:bg-red-700 disabled:opacity-50"
                          >
                            {restoring ? 'Restoring…' : 'Restore'}
                          </button>
                          <button
                            onClick={() => setConfirmRestore(false)}
                            disabled={restoring}
                            className="px-2 py-0.5 rounded border border-red-300 bg-white text-red-700 text-[11px] hover:bg-red-100 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <ValidationPanel
                    validating={validating}
                    validation={validation}
                  />
                </div>
              ) : (
                <>
                  <SystemMessageView
                    systemMessage={
                      activeVersion?.system_message ?? activeEntry.system_message ?? ''
                    }
                  />
                  <div className="mt-4 mb-1 flex items-center justify-between">
                    <h4 className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                      User Template
                    </h4>
                  </div>
                  <pre
                    data-testid="prompt-template-view"
                    className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-xs font-mono whitespace-pre-wrap leading-relaxed max-h-[50vh] overflow-y-auto"
                  >
                    {activeVersion?.template}
                  </pre>
                  {activeVersion?.notes && (
                    <div className="mt-3 text-xs text-zinc-600">
                      <span className="text-zinc-400 mr-1">Notes:</span>{activeVersion.notes}
                    </div>
                  )}

                  {showHistory && (
                    <div className="mt-5 border-t border-zinc-200 pt-4" data-testid="prompt-history-panel">
                      <h4 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Version History</h4>
                      <div className="space-y-3">
                        {[...activeEntry.versions].reverse().map(v => {
                          const prev = activeEntry.versions.find(p => p.version === v.version - 1);
                          const isActive = v.version === activeEntry.active_version;
                          return (
                            <div
                              key={v.version}
                              data-testid={`prompt-history-row-${v.version}`}
                              className={`border rounded-sm p-3 ${isActive ? 'border-blue-200 bg-blue-50/40' : 'border-zinc-200'}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="text-xs text-zinc-700">
                                  <span className="font-mono">v{v.version}</span>
                                  <span className="text-zinc-400 ml-2">{new Date(v.created_at).toLocaleString()}</span>
                                  {isActive && <span className="ml-2 px-1.5 py-0.5 bg-[#002FA7] text-white text-[10px] rounded-sm">ACTIVE</span>}
                                </div>
                                {!isActive && (
                                  <button
                                    data-testid={`prompt-activate-v${v.version}`}
                                    onClick={() => handleActivate(v.version)}
                                    className="text-xs flex items-center gap-1 px-2 py-1 border border-zinc-300 rounded-sm hover:bg-white"
                                  >
                                    <RotateCcw size={11} /> Activate
                                  </button>
                                )}
                              </div>
                              {v.notes && (
                                <div className="text-xs text-zinc-600 italic mb-2">{v.notes}</div>
                              )}
                              {prev ? (
                                <pre className="bg-white border border-zinc-100 rounded-sm p-2 text-[11px] font-mono whitespace-pre-wrap max-h-40 overflow-y-auto">
                                  {diffLines(prev.template, v.template).map((d, i) => (
                                    <span
                                      key={i}
                                      className={
                                        d.sign === '+' ? 'bg-green-50 text-green-800 block' :
                                        d.sign === '-' ? 'bg-red-50 text-red-800 block line-through opacity-70' :
                                        'block text-zinc-600'
                                      }
                                    >
                                      {d.sign} {d.text}
                                    </span>
                                  ))}
                                </pre>
                              ) : (
                                <pre className="bg-white border border-zinc-100 rounded-sm p-2 text-[11px] font-mono whitespace-pre-wrap max-h-40 overflow-y-auto text-zinc-600">
                                  {v.template}
                                </pre>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </section>
      </div>

      {testOpen && activeEntry && (
        <PromptTestModal
          promptKey={activeEntry.id}
          variables={activeEntry.variables}
          tier={activeEntry.tier as 'fast' | 'capable'}
          onClose={() => setTestOpen(false)}
        />
      )}

      {/* Phase 13B-second Chunk 2 — Reset all to seed confirmation
          modal. Destructive action; requires explicit click-through.
          History is preserved server-side via an edit_history marker
          per row, so a future audit can still see "admin edited X
          then reset to seed v3 on YYYY-MM-DD". */}
      {confirmResetAll && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
          role="dialog"
          aria-modal="true"
          data-testid="prompt-reset-all-confirm"
        >
          <div className="w-full max-w-md rounded-sm border border-zinc-200 bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-base font-semibold text-rose-700">
              Reset all prompts to seed v3?
            </h2>
            <p className="mb-4 text-sm text-zinc-600">
              This rewrites every prompt's content from
              <code className="mx-1 rounded-sm bg-zinc-100 px-1">seeds/prompts_v3.json</code>.
              Live edits made through this UI will be lost; edit
              history is preserved as an audit marker.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-sm border border-zinc-300 bg-white px-3 py-1.5 text-xs text-zinc-700"
                onClick={() => setConfirmResetAll(false)}
                data-testid="prompt-reset-all-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={resettingAll}
                className="rounded-sm bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                onClick={async () => {
                  setResettingAll(true);
                  setError(null);
                  try {
                    const result = await api.resetAllPromptsToSeed();
                    setConfirmResetAll(false);
                    setFlash(
                      `Reset ${result.prompts_reset} prompts to seed v3${
                        result.skipped.length
                          ? ` · ${result.skipped.length} admin-added rows skipped`
                          : ''
                      }`,
                    );
                    // Reload the prompts table.
                    const fresh = await api.getPrompts();
                    setPrompts(fresh);
                  } catch (e: any) {
                    setError(
                      e?.response?.data?.detail?.message
                        ?? e?.response?.data?.detail
                        ?? e?.message
                        ?? 'Reset failed',
                    );
                  } finally {
                    setResettingAll(false);
                  }
                }}
                data-testid="prompt-reset-all-confirm-btn"
              >
                {resettingAll ? 'Resetting…' : 'Reset all'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Test prompt modal ────────────────────────────────────────────────────

function PromptTestModal({ promptKey, variables, tier, onClose }: {
  promptKey: string;
  variables: string[];
  tier: 'fast' | 'capable';
  onClose: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(variables.map(v => [v, '']))
  );
  const [jsonMode, setJsonMode] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{ rendered: string; response: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const handleRun = async () => {
    setRunning(true);
    setErr(null);
    try {
      const res = await api.testPrompt(promptKey, values, { tier, json_mode: jsonMode });
      setResult(res);
    } catch (e: any) {
      setErr(apiErrorText(e, 'Test failed'));
    }
    setRunning(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" data-testid="prompt-test-modal">
      <div className="bg-white rounded-sm w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
          <h3 className="text-sm font-medium text-zinc-900">Test prompt: {promptKey}</h3>
          <button onClick={onClose} data-testid="prompt-test-close" className="text-zinc-400 hover:text-zinc-900"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <h4 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Variables</h4>
            {variables.length === 0 ? (
              <p className="text-xs text-zinc-500 italic">This prompt has no variables.</p>
            ) : (
              <div className="space-y-2">
                {variables.map(v => (
                  <div key={v}>
                    <label className="text-xs text-zinc-500 block mb-1 font-mono">{`{${v}}`}</label>
                    <textarea
                      data-testid={`prompt-test-var-${v}`}
                      value={values[v] || ''}
                      onChange={(e) => setValues(prev => ({ ...prev, [v]: e.target.value }))}
                      rows={2}
                      className="w-full bg-white border border-zinc-300 rounded-sm px-2 py-1.5 text-xs font-mono"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-600">
            <input
              type="checkbox"
              data-testid="prompt-test-json-mode"
              checked={jsonMode}
              onChange={(e) => setJsonMode(e.target.checked)}
            />
            Ask LLM to return JSON
          </label>
          <button
            data-testid="prompt-test-run"
            onClick={handleRun}
            disabled={running}
            className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-3 py-1.5 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            Run on {tier} tier
          </button>
          {err && (
            <div className="p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-800 flex items-center gap-2">
              <AlertTriangle size={14} /> {err}
            </div>
          )}
          {result && (
            <div className="space-y-3" data-testid="prompt-test-result">
              <div>
                <h4 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Rendered prompt</h4>
                <pre className="bg-zinc-50 border border-zinc-200 rounded-sm p-2 text-[11px] font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">{result.rendered}</pre>
              </div>
              <div>
                <h4 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">LLM response</h4>
                <pre className="bg-green-50 border border-green-200 rounded-sm p-2 text-[11px] font-mono whitespace-pre-wrap max-h-64 overflow-y-auto">{result.response}</pre>
              </div>
            </div>
          )}
        </div>
      </div>
      <AppFooter />
    </div>
  );
}
